import CountryCodePicker from "@/components/drop-downs/CountryCodePicker";
import TimezonePicker from "@/components/drop-downs/TimezonePicker";
import { useGetModulesData, usePermissionCheck } from "@/hooks/AllCustomHooks";
import { useContext, useEffect, useRef, useState } from "react";
import { FaCaretDown } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import AddSellersToBuybox from "./AddSellersToBuybox";
import {
    IAdminToggleBtn,
    IChannel,
    IMainContext,
    ParentModule,
    SETTINGS_TABLE_DATA_TYPE,
    TClientTimezone,
    TSellersData,
    TBrandsData,
} from "@/ts/common-types";
import { mainContext } from "@/ts/constants";
import {
    removeTimezoneDefaultStyle,
    submitSettings,
    titleCase,
} from "@/ts/utils";
import { updateTitle } from "@/features/titleSlice";
import { updateActiveModule } from "@/features/activeModuleSlice";
import { populateBrandsListData, populateSellersData } from "@/ts/retrieve-data";
import * as Sentry from "@sentry/react";

const GeneralSettings = () => {
    const dispatch = useDispatch();
    const timezoneRef = useRef(null);
    const countryRef = useRef(null);

    const buyboxView = useGetModulesData({ type: "additional-features" })
        .additionalFeaturesData["buybox-add-sellers"];

    const userCountryDetails = useSelector(
        (state: any) => state.userCountryDetails.value
    );
    const clientTimezone = useSelector(
        (state: TClientTimezone) => state.clientTimezone.value
    );
    const enableBuybox = useSelector(
        (state: IAdminToggleBtn) => state.enableBuybox.value
    );
    const channel = useSelector((state: IChannel) => state.channel.value);

    const [selectedCountryDetails, setSelectedCountryDetails] =
        useState(userCountryDetails);

    const [isLoading, setIsLoading] = useState<{
        sellers: boolean;
        brands: boolean;
    }>({
        sellers: true,
        brands: true,
    });
    const [sellersData, setSellersData] = useState([]);
    const [brandsData, setBrandsData] = useState([]);
    const { selectedSellersData, setSelectedSellersData, selectedBrandsData, setSelectedBrandsData, cerbosPermissions } =
        useContext<IMainContext>(mainContext);

    const [asinsAvailable, setAsinsAvailable] = useState<boolean>(false);

    const { canUpdateSettings } = usePermissionCheck();

    const sortData = () => {
        setSellersData((prevData) => {
            const sorted = [...prevData] as TSellersData[];
            const selected = sorted
                .filter((item) => item.isPreferredSeller)
                .sort((a, b) => a.seller_name.localeCompare(b.seller_name));
            const unselected = sorted
                .filter((item) => !item.isPreferredSeller)
                .sort((a, b) => a.seller_name.localeCompare(b.seller_name));
            return [...selected, ...unselected];
        });
        setBrandsData((prevData) => {
            const sorted = [...prevData] as TBrandsData[];
            const selected = sorted
                .filter((item) => item.is_selected)
                .sort((a, b) => a.tag_name.localeCompare(b.tag_name));
            const unselected = sorted
                .filter((item) => !item.is_selected)
                .sort((a, b) => a.tag_name.localeCompare(b.tag_name));
            return [...selected, ...unselected];
        });
    };

    useEffect(() => {
        dispatch(updateTitle(titleCase(ParentModule.SETTINGS)));
        dispatch(updateActiveModule(ParentModule.SETTINGS));
        removeTimezoneDefaultStyle();
        if (buyboxView) {
            setIsLoading((prev) => ({ ...prev, sellers: true }));
            populateSellersData()
                .then((data) => {
                    if (data.status) {
                        const { sellerDetails, hasAsin } = data.message;
                        setAsinsAvailable(hasAsin);
                        setSellersData(sellerDetails);
                        setSelectedSellersData(
                            sellerDetails
                                .map((sellerData: TSellersData) => {
                                    const selectedSellerName = [];
                                    if (sellerData.isPreferredSeller) {
                                        selectedSellerName.push(
                                            sellerData.seller_name
                                        );
                                    }
                                    return selectedSellerName;
                                })
                                .filter((item: string[]) => item.length)
                                .flat()
                        );
                    } else {
                        setSellersData([]);
                    }
                })
                .catch((error) => {
                    console.error(error);
                    Sentry.captureException(error);
                }).finally(() => {
                    setIsLoading((prev) => ({ ...prev, sellers: false }));
                });
        }

        setIsLoading((prev) => ({ ...prev, brands: true }));
        populateBrandsListData().then((response) => {
            if (response.status) {
                const data = response.data;
                
                setBrandsData(data);
                setSelectedBrandsData(
                    data
                        .map((brandData: TBrandsData) => {
                            const selectedBrandId = [];
                            if (brandData.is_selected) {
                                selectedBrandId.push(
                                    brandData.id
                                );
                            }
                            return selectedBrandId;
                        })
                        .filter((item: string[]) => item.length)
                        .flat()
                );
            } else {
                setBrandsData([]);
            }
        }).catch((error) => {
            console.error("Error populating brands data", error.message);
            Sentry.captureException(error);
        }).finally(() => {
            setIsLoading((prev) => ({ ...prev, brands: false }));
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, enableBuybox, channel]);

    useEffect(() => {
        setSelectedCountryDetails(userCountryDetails);
    }, [userCountryDetails]);

    return (
        <div style={{
                    height: "calc(100vh - 205px)",
                    overflowY: "auto",
                }}>
            <div className="timezone-container mt-3">
                <label className="mb-3 settings-timezone-header">
                    Set time zone
                </label>
                <div id="timezone-container-element">
                    <div
                        id="timezone-div"
                        onClick={() => {
                            if (timezoneRef.current) {
                                timezoneRef.current.focus();
                            }
                        }}
                    >
                        <div className="mx-2 mt-1 text-secondary">
                            Primary time zone
                        </div>
                        <TimezonePicker
                            timezoneRef={timezoneRef}
                            fetchData={true}
                            style={{
                                fontSize: "1.3rem",
                                border: "none",
                            }}
                        />
                    </div>
                    <div id="drop-down-caret-down">
                        <FaCaretDown />
                    </div>
                </div>
            </div>

            <div className="country-code-container mt-3">
                <label className="mb-3 settings-timezone-header">
                    Set country locale
                </label>
                <div id="country-picker-element">
                    <div
                        id="country-picker-div"
                        onClick={() => {
                            if (countryRef.current) {
                                countryRef.current.focus();
                            }
                        }}
                    >
                        <div className="mx-2 mt-1 text-secondary">
                            Primary country locale
                        </div>
                        <CountryCodePicker
                            {...{
                                selectedCountryDetails,
                                setSelectedCountryDetails,
                                countryRef,
                            }}
                            fetchData={true}
                            style={{
                                fontSize: "1.3rem",
                                border: "none",
                            }}
                        />
                    </div>
                    <div id="drop-down-caret-down">
                        <FaCaretDown />
                    </div>
                </div>
            </div>

            {buyboxView && (
                <AddSellersToBuybox
                    {...{
                        isLoading: isLoading.sellers,
                        listData: sellersData,
                        selectedDatalist: selectedSellersData,
                    }}
                />
            )}
            <AddSellersToBuybox
                    {...{
                        isLoading: isLoading.brands,
                        listData: brandsData,
                        selectedDatalist: selectedBrandsData,
                        title: SETTINGS_TABLE_DATA_TYPE.BRANDS,
                    }}
                />
            <button
                className="btn btn-primary save-settings"
                type="submit"
                onClick={() =>
                    submitSettings(
                        selectedSellersData,
                        clientTimezone,
                        asinsAvailable,
                        selectedCountryDetails,
                        selectedBrandsData,
                        sortData
                    )
                }
                disabled={!canUpdateSettings}
            >
                Save
            </button>
        </div>
    );
};

export default GeneralSettings;
